import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle, Circle, PencilSimple, Trash, ShieldCheck } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import ProductForm from "./ProductForm";
import StepPanel from "./StepPanel";
import { useCurrencySymbol } from "@/hooks/useCurrencySymbol";

export default function ProductDetail() {
  const currencySymbol = useCurrencySymbol();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [activeStep, setActiveStep] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => (await api.get(`/products/${id}`)).data,
  });
  const { data: meta } = useQuery({
    queryKey: ["steps-meta"],
    queryFn: async () => (await api.get("/steps/meta")).data,
    staleTime: Infinity,
  });

  const groups = useMemo(() => {
    if (!meta) return [];
    const map = {};
    meta.forEach((s) => { (map[s.group] = map[s.group] || []).push(s); });
    return Object.entries(map);
  }, [meta]);

  if (isLoading || !product || !meta) {
    return <div className="py-16 text-center text-sm text-slate-400" data-testid="product-loading">Loading product…</div>;
  }

  const current = meta.find((s) => s.key === activeStep) || meta[0];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["product", id] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      navigate("/products");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const stepState = (key) => product.steps?.[key];

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Link to="/products" data-testid="back-to-products" className="mt-1 rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tighter text-slate-900" data-testid="product-name">{product.name}</h1>
              <StatusBadge status={product.status} />
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {product.sku && <span className="mr-3">SKU: {product.sku}</span>}
              {product.client_name && <span className="mr-3">Client: {product.client_name}</span>}
              <span>{product.egg_type === "egg" ? "Egg" : "Eggless"} · {product.diet_type === "non_veg" ? "Non-Veg" : "Veg"}</span>
              {product.price > 0 && <span className="ml-3 font-semibold text-slate-700">{currencySymbol}{product.price}</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {can("products.edit") && (
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-product-button">
              <PencilSimple size={16} className="mr-1" /> Edit
            </Button>
          )}
          {can("products.delete") && (
            <Button variant="outline" onClick={remove} data-testid="delete-product-button" className="text-red-600 hover:bg-red-50">
              <Trash size={16} className="mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="pipeline-progress">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Pipeline progress</span>
          <span className="text-sm font-bold text-slate-900" data-testid="progress-text">{product.completed_steps}/{product.total_steps} · {product.progress}%</span>
        </div>
        <ProgressBar value={product.progress} />
        {product.status === "published" && (
          <p className="mt-2 text-xs font-semibold text-[#047857]" data-testid="published-message">All 23 steps complete — product is published.</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        <div className="rounded-md border border-slate-200 bg-white" data-testid="steps-stepper">
          <div className="border-b border-slate-200 px-4 py-3">
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">23 Pipeline Steps</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-2">
            {groups.map(([group, steps]) => (
              <div key={group} className="mb-3">
                <div className="px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{group}</div>
                {steps.map((s) => {
                  const done = stepState(s.key)?.completed;
                  const active = current.key === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setActiveStep(s.key)}
                      data-testid={`step-item-${s.key}`}
                      className={`flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors duration-150 ${
                        active ? "bg-[#FFF7ED] font-semibold text-[#C2410C]" : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {done ? (
                        <CheckCircle size={18} weight="fill" className="shrink-0 text-[#10B981]" />
                      ) : active ? (
                        <Circle size={18} weight="fill" className="shrink-0 text-[#EA580C]" />
                      ) : (
                        <Circle size={18} className="shrink-0 text-slate-300" />
                      )}
                      <span className="flex-1 truncate">{s.order}. {s.title}</span>
                      {s.approval && <ShieldCheck size={14} className="shrink-0 text-slate-400" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <StepPanel product={product} stepMeta={current} onSaved={refresh} />
      </div>

      <ProductForm open={editOpen} onOpenChange={setEditOpen} product={product} onSaved={refresh} />
    </div>
  );
}
